// API service para comunicação com o waba-manager worker
const API_BASE_URL = process.env.NODE_ENV === 'production' ? 'https://waba-manager-production.auroraid.workers.dev' : 'http://localhost:30001';

export interface WhatsAppAccount {
    id: number;
    publicId: string;
    name: string;
    facebookUserId?: string;
    facebookPageUserId?: string;
    facebookUserToken?: string;
    metaBusinessAccountId?: string;
    tokenMeta?: string;
    channel: string;
    status: 'CONNECTED' | 'DISCONNECTED' | 'PENDING';
    queueIds: string[];
    companyId: string;
    number?: string;
    botId?: string;
    createdAt: string;
    updatedAt: string;
}

export interface PhoneNumber {
    id: number;
    publicId: string;
    phoneNumberId: string;
    wabaAccountId: string;
    verifiedName?: string;
    throughput?: string;
    qualityRating?: string;
    displayPhoneNumber: string;
    messagingLimitTier?: string;
    whatsappAccountId?: string;
    companyId: string;
    isRegistered: boolean;
    isSubscribed: boolean;
    pin?: string;
    webhookUrl?: string;
    createdAt: string;
    updatedAt: string;
}

export interface CreateWhatsAppRequest {
    name: string;
    queueId?: string;
    botId?: string;
    status?: 'CONNECTED' | 'DISCONNECTED' | 'PENDING';
    token?: string;
}

export interface CreatePhoneNumberRequest {
    wabaAccountId: string;
    accessToken: string;
}

export interface StoreWhatsAppRequest {
    code: string;
}

export interface StoreFacebookRequest {
    code: string;
    userId?: string;
    addInstagram?: boolean;
}

class ApiService {
    private companyId: string;

    constructor(companyId: string = 'default-company') {
        this.companyId = companyId;
    }

    private async request<T>(
        endpoint: string,
        options: RequestInit = {}
    ): Promise<T> {
        const url = `${API_BASE_URL}${endpoint}`;

        const response = await fetch(url, {
            ...options,
            headers: {
                'Content-Type': 'application/json',
                'x-company-id': this.companyId,
                'x-api-key': '2731175a-12bf-493d-b4db-9422b7cb59f5',
                ...options.headers,
            },
        });

        if (!response.ok) {
            const error = await response.json().catch(() => ({ error: 'Network error' }));
            throw new Error(error.error || `HTTP ${response.status}`);
        }

        return response.json();
    }

    // WhatsApp Accounts
    async getWhatsAppAccounts(): Promise<WhatsAppAccount[]> {
        return this.request<WhatsAppAccount[]>('/whatsapp');
    }

    async getWhatsAppAccount(id: string): Promise<WhatsAppAccount> {
        return this.request<WhatsAppAccount>(`/whatsapp/${id}`);
    }

    async createWhatsAppAccount(data: CreateWhatsAppRequest): Promise<WhatsAppAccount> {
        return this.request<WhatsAppAccount>('/whatsapp', {
            method: 'POST',
            body: JSON.stringify(data),
        });
    }

    async updateWhatsAppAccount(id: string, data: Partial<CreateWhatsAppRequest>): Promise<WhatsAppAccount> {
        return this.request<WhatsAppAccount>(`/whatsapp/${id}`, {
            method: 'PUT',
            body: JSON.stringify(data),
        });
    }

    async deleteWhatsAppAccount(id: string): Promise<void> {
        return this.request<void>(`/whatsapp/${id}`, {
            method: 'DELETE',
        });
    }

    // Facebook Integration
    async generateAuthUrl(): Promise<{ url: string }> {
        return this.request<{ url: string }>('/whatsapp/generate-url', {
            method: 'POST',
        });
    }

    async storeWhatsAppAccount(data: StoreWhatsAppRequest): Promise<{ success: boolean }> {
        return this.request<{ success: boolean }>('/whatsapp/store', {
            method: 'POST',
            body: JSON.stringify(data),
        });
    }

    async storeFacebookAccount(data: StoreFacebookRequest): Promise<{ success: boolean }> {
        return this.request<{ success: boolean }>('/whatsapp/store-facebook', {
            method: 'POST',
            body: JSON.stringify(data),
        });
    }

    // Phone Numbers
    async getPhoneNumbers(): Promise<PhoneNumber[]> {
        return this.request<PhoneNumber[]>('/whatsapp/phone-numbers');
    }

    async createPhoneNumbers(data: CreatePhoneNumberRequest): Promise<PhoneNumber[]> {
        return this.request<PhoneNumber[]>('/whatsapp/phone-numbers', {
            method: 'POST',
            body: JSON.stringify(data),
        });
    }

    // Register and Subscribe
    async registerPhoneNumber(id: string, pin: string): Promise<{ success: boolean; message: string }> {
        return this.request<{ success: boolean; message: string }>(`/whatsapp/${id}/register`, {
            method: 'POST',
            body: JSON.stringify({ pin }),
        });
    }

    async subscribeApp(id: string): Promise<{ success: boolean; message: string }> {
        return this.request<{ success: boolean; message: string }>(`/whatsapp/${id}/subscribe`, {
            method: 'POST',
        });
    }

    // Webhook Configuration
    async updateWebhook(phoneNumberId: string, webhookUrl: string): Promise<{ success: boolean; webhookUrl?: string }> {
        return this.request<{ success: boolean; webhookUrl?: string }>(`/whatsapp/phone-numbers/${phoneNumberId}/webhook`, {
            method: 'PUT',
            body: JSON.stringify({ webhookUrl }),
        });
    }

    // Health Check
    async healthCheck(): Promise<{ status: string; timestamp: string }> {
        return this.request<{ status: string; timestamp: string }>('/health');
    }
}

export const apiService = new ApiService();
export default ApiService;
